<script setup lang="ts">
import { createApp, h, onBeforeUnmount, onMounted, ref, type App } from 'vue';
import type { Map as MapLibreMap, Marker } from 'maplibre-gl';
import { COMPANY_ZOOM, DEFAULT_CENTRE, DEFAULT_ZOOM, createStyle, type BaseLayer } from '@/map/mapStyle';
import { debounce } from '@/lib/debounce';
import { getWeather } from '@/services/weather';
import type { CompanyInfo } from '@/services/types';
import MapInfo from './MapInfo.vue';

// MapLibre's own stylesheet. Not optional: without it the canvas keeps its
// device-pixel width attribute as its intrinsic size, which blows out any grid it
// sits in and squeezes sibling columns to a sliver, and the attribution and
// control containers render unpositioned instead of docked in the map corners.
//
// Imported statically even though the library itself is lazy. A dynamic CSS
// import becomes its own stylesheet, and the assetFileNames rule names every CSS
// asset app.css, so the second one was auto-numbered to app2.css — an arbitrary
// name with no content hash, which would never cache-bust. Folding it into the
// single entry stylesheet keeps Razor's asp-append-version in charge of that, and
// 40 KB on a sheet already dominated by Semantic is not worth the fragility.
import 'maplibre-gl/dist/maplibre-gl.css';

/**
 * The demo panel's map.
 *
 * Converted from map.vue and moved from Google Maps to MapLibre with Kartverket
 * tiles, which removes the API key and the billing account along with it.
 *
 * MapLibre is imported dynamically so its ~800 KB stays out of the main bundle;
 * only visitors who open the demo panel pay for it.
 */

const POSITION_DECIMALS = 4;

const mapHost = ref<HTMLElement | null>(null);
const latitude = ref('');
const longitude = ref('');
const temperature = ref('');
const symbolCode = ref('');
const layer = ref<BaseLayer>('topo');

let map: MapLibreMap | null = null;
let companyMarker: Marker | null = null;
let infoApp: App | null = null;
let weatherAbort: AbortController | null = null;

function updateCentreReadout(): void {
  if (!map) {
    return;
  }

  const centre = map.getCenter();
  latitude.value = centre.lat.toFixed(POSITION_DECIMALS);
  longitude.value = centre.lng.toFixed(POSITION_DECIMALS);
}

async function refreshWeather(): Promise<void> {
  if (!map) {
    return;
  }

  // Panning fires repeatedly; abandon the in-flight request rather than racing
  // it against the newer one.
  weatherAbort?.abort();
  weatherAbort = new AbortController();

  const centre = map.getCenter();

  try {
    const result = await getWeather(centre.lat, centre.lng, weatherAbort.signal);

    temperature.value = result?.airTemperature?.toFixed(1) ?? '';
    symbolCode.value = result?.symbolCode ?? '';
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      return;
    }

    temperature.value = '';
    symbolCode.value = '';
    console.warn('Kunne ikke hente værdata', error);
  }
}

const refreshWeatherDebounced = debounce(() => void refreshWeather(), 400);

function onMoveEnd(): void {
  updateCentreReadout();
  refreshWeatherDebounced();
}

function onLayerChange(next: BaseLayer): void {
  layer.value = next;
  map?.setStyle(createStyle(next));
}

/**
 * Mounts MapInfo as a MapLibre control.
 *
 * The Google version pushed a manually `$mount()`ed Vue 2 instance into
 * `map.controls[position]`. MapLibre takes an IControl, so the element is owned
 * by a small standalone Vue app whose props are re-rendered from this
 * component's refs.
 */
function addInfoControl(target: MapLibreMap): void {
  const container = document.createElement('div');
  container.className = 'maplibregl-ctrl';

  infoApp = createApp({
    render: () =>
      h(MapInfo, {
        latitude: latitude.value,
        longitude: longitude.value,
        temperature: temperature.value,
        symbolCode: symbolCode.value,
        layer: layer.value,
        'onLayer-change': onLayerChange,
      }),
  });

  infoApp.mount(container);

  target.addControl({
    onAdd: () => container,
    onRemove: () => container.remove(),
  });
}

/** Focuses a company and drops a marker on it. */
async function setCompanyMarker(company: CompanyInfo): Promise<void> {
  if (!map || !company.location) {
    return;
  }

  const { default: maplibregl } = await import('maplibre-gl');
  const position: [number, number] = [company.location.longitude, company.location.latitude];

  if (companyMarker) {
    companyMarker.setLngLat(position);
  } else {
    companyMarker = new maplibregl.Marker({ color: '#4b2e6f' }).setLngLat(position).addTo(map);
  }

  companyMarker.setPopup(
    new maplibregl.Popup({ offset: 24 }).setText(
      [company.name, company.address, company.postalArea].filter(Boolean).join(', '),
    ),
  );

  map.flyTo({ center: position, zoom: COMPANY_ZOOM });
}

onMounted(async () => {
  if (!mapHost.value) {
    return;
  }

  const { default: maplibregl } = await import('maplibre-gl');

  map = new maplibregl.Map({
    container: mapHost.value,
    style: createStyle(layer.value),
    center: DEFAULT_CENTRE,
    zoom: DEFAULT_ZOOM,
    attributionControl: { compact: true },
  });

  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
  map.on('moveend', onMoveEnd);

  addInfoControl(map);
  updateCentreReadout();
  refreshWeatherDebounced();
});

onBeforeUnmount(() => {
  weatherAbort?.abort();
  infoApp?.unmount();
  companyMarker?.remove();
  map?.remove();
});

defineExpose({ setCompanyMarker });
</script>


<template>
  <div class="map-wrap">
    <!-- A CSS crosshair beats the Google version's marker bound to map centre:
         nothing to keep in sync, and it cannot be dragged. -->
    <div ref="mapHost" class="map-canvas" />
    <div class="map-crosshair" aria-hidden="true" />
  </div>
</template>

<style>
.map-wrap {
  position: relative;
}

.map-canvas {
  height: 400px;
}

.map-crosshair {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 22px;
  height: 22px;
  margin: -11px 0 0 -11px;
  pointer-events: none;
  z-index: 2;
}

.map-crosshair::before,
.map-crosshair::after {
  content: '';
  position: absolute;
  background: rgba(0, 0, 0, 0.55);
}

.map-crosshair::before {
  top: 50%;
  left: 0;
  width: 100%;
  height: 1px;
}

.map-crosshair::after {
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
}
</style>
